import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, ToggleButton, ToggleButtonGroup,
  CircularProgress
} from '@mui/material';

interface DailyStat {
  date: string;
  count: number;
  iosCount: number;
  androidCount: number;
}

const getApiBaseUrl = () => {
  const environment = process.env.REACT_APP_FIREBASE_ENV || 'test';
  switch (environment) {
    case 'production':
      return 'https://adminapi-a2b5o5ydga-uc.a.run.app';
    case 'test':
      return 'https://adminapi-xjh3gncbvq-uc.a.run.app';
    default:
      return 'https://adminapi-xjh3gncbvq-uc.a.run.app';
  }
};

const getAuthToken = () => localStorage.getItem('admin_token') || '';

const TutorialStatsPage: React.FC = () => {
  const [stats, setStats] = useState<DailyStat[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<'7' | '30' | '90'>('7');

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/tutorial-stats?days=${period}`,
        {
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      setStats(data.stats || []);
      setTotalCount(data.totalCount || 0);
    } catch (e) {
      console.error('Error loading tutorial stats:', e);
      setStats([]);
      setTotalCount(0);
    }
    setLoading(false);
  }, [period]);

  useEffect(() => { loadStats(); }, [loadStats]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>チュートリアル統計</Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        「はじめる」ボタンのタップ数（デバイス単位で重複除外）
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <ToggleButtonGroup value={period} exclusive onChange={(_, v) => v && setPeriod(v)} size="small">
          <ToggleButton value="7">7日間</ToggleButton>
          <ToggleButton value="30">30日間</ToggleButton>
          <ToggleButton value="90">90日間</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="h6">合計: {totalCount}人</Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>日付</TableCell>
                <TableCell align="right">合計</TableCell>
                <TableCell align="right">iOS</TableCell>
                <TableCell align="right">Android</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stats.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">データがありません</TableCell>
                </TableRow>
              ) : (
                stats.map((stat) => (
                  <TableRow key={stat.date}>
                    <TableCell>{stat.date}</TableCell>
                    <TableCell align="right"><strong>{stat.count}</strong></TableCell>
                    <TableCell align="right">{stat.iosCount}</TableCell>
                    <TableCell align="right">{stat.androidCount}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default TutorialStatsPage;
